/**
 * ITサポートの共通定義（チャネル・カテゴリ・緊急度・ステータス・型・判定）。
 * 申請フォーム／受付一覧／通知で共有する。
 *
 * 「IT管理者」「IT開発者」はロールではなく profiles のフラグ列
 * （is_it_admin / is_it_developer）で表現し、どのロールにも付与できる。
 */

import { supabase } from '@/lib/supabase';
import { normalizeAppRole } from '@/lib/roles';
import { createStorageSignedUrl, removeStorageSignedUrlCache } from '@/lib/storageSignedUrl';

export type ItChannel = 'online' | 'ai' | 'direct' | 'bug_report' | 'feature_request';

export type ItUrgency = 'low' | 'normal' | 'high' | 'urgent';

export type ItStatus =
  | 'pending'
  | 'accepted'
  | 'in_progress'
  | 'waiting'
  | 'resolved'
  | 'closed'
  | 'cancelled';

export type ItTargetGroup = 'it_admin' | 'it_developer';

export type ItSenderType = 'requester' | 'it' | 'ai' | 'system';

export interface ItSupportRequest {
  id: string;
  requester_id: string;
  office_id: string | null;
  channel: ItChannel;
  category: string | null;
  title: string;
  body: string;
  urgency: ItUrgency;
  desired_at: string | null;
  target_group: ItTargetGroup;
  status: ItStatus;
  assignee_id: string | null;
  ai_turn_count: number;
  direct_contact_unlocked: boolean;
  meeting_room_name: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  /** 申請者プロフィール（結合取得）。削除済み等は null。 */
  requester?: { username: string | null; furigana?: string | null; role?: string | null } | null;
}

/** 申請カテゴリの選択肢（§3.4）。 */
export const IT_CATEGORIES = [
  'アカウント・ログイン',
  '端末・PC',
  'ネットワーク',
  '印刷',
  'メール',
  'グループウェア操作',
  'データ・権限',
  'その他',
] as const;

/** 緊急度の選択肢と表示メタ（青・紫を避けた配色）。 */
export const IT_URGENCY_OPTIONS: ItUrgency[] = ['low', 'normal', 'high', 'urgent'];

export const IT_URGENCY_META: Record<ItUrgency, { label: string; className: string; icon: string }> = {
  low: { label: '低', className: 'bg-gray-100 text-gray-600', icon: 'ri-arrow-down-line' },
  normal: { label: '中', className: 'bg-emerald-50 text-emerald-700', icon: 'ri-subtract-line' },
  high: { label: '高', className: 'bg-amber-100 text-amber-700', icon: 'ri-arrow-up-line' },
  urgent: { label: '緊急', className: 'bg-red-100 text-red-700', icon: 'ri-alarm-warning-line' },
};

/** 申請ステータスの表示メタ。テーマトーンに統一する。 */
export const IT_STATUS_META: Record<ItStatus, { label: string; className: string }> = {
  pending: { label: '受付', className: 'bg-gray-100 text-gray-600' },
  accepted: {
    label: '対応予定',
    className: 'bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)]',
  },
  in_progress: {
    label: '対応中',
    className: 'bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)]',
  },
  waiting: { label: '申請者待ち', className: 'bg-amber-100 text-amber-700' },
  resolved: { label: '解決', className: 'bg-[color:var(--theme-primary)] text-white' },
  closed: { label: '完了', className: 'bg-[color:var(--theme-primary)] text-white' },
  cancelled: { label: '取消', className: 'bg-gray-100 text-gray-500' },
};

/** チャネルの表示ラベル（§3）。 */
export const IT_CHANNEL_LABEL: Record<ItChannel, string> = {
  online: 'オンライン申請',
  ai: 'AIサポート',
  direct: '直接連絡',
  bug_report: '不具合報告',
  feature_request: '機能追加の要望',
};

export const getItUrgencyMeta = (urgency: string) =>
  IT_URGENCY_META[urgency as ItUrgency] ?? IT_URGENCY_META.normal;

export const getItStatusMeta = (status: string) =>
  IT_STATUS_META[status as ItStatus] ?? IT_STATUS_META.pending;

/** IT担当者（IT管理者 or IT開発者）か。ロール非依存のフラグで判定する。 */
export const isItStaff = (
  profile?: { is_it_admin?: boolean | null; is_it_developer?: boolean | null } | null,
): boolean => Boolean(profile?.is_it_admin || profile?.is_it_developer);

/** IT管理者か。 */
export const isItAdmin = (profile?: { is_it_admin?: boolean | null } | null): boolean =>
  Boolean(profile?.is_it_admin);

/** IT開発者か。 */
export const isItDeveloper = (profile?: { is_it_developer?: boolean | null } | null): boolean =>
  Boolean(profile?.is_it_developer);

/** 日時（ISO）を日本語表示（YYYY/MM/DD HH:mm）へ整形する。未指定は「—」。 */
export const formatItDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * AIチャットで「IT担当者に直接連絡」ボタンが開放されるターン数（ユーザー発言数）。
 * §3.5 の確定事項（5回の数え方＝ユーザー発言数）に合わせる。
 */
export const IT_AI_DIRECT_CONTACT_TURN_LIMIT = 5;

/** AIチャットのメッセージ（画面表示用）。sender_type を表示ロールへ丸めたもの。 */
export interface ItAiChatMessage {
  id: string;
  role: 'requester' | 'ai' | 'system';
  body: string;
  createdAt: string;
}

/** DBの sender_type を画面表示用のロールへ変換する。 */
export const toItAiChatRole = (senderType: string): ItAiChatMessage['role'] => {
  if (senderType === 'requester') return 'requester';
  if (senderType === 'system') return 'system';
  return 'ai';
};

/** スレッドの1メッセージ（DBの it_support_messages 行）。 */
export interface ItSupportMessage {
  id: string;
  request_id: string;
  sender_id: string | null;
  sender_type: ItSenderType;
  body: string;
  meta: Record<string, unknown> | null;
  created_at: string;
  /** 送信者プロフィール（結合取得）。削除済み等は null。 */
  sender?: { username: string | null } | null;
}

/** 添付ファイル（DBの it_support_attachments 行）。 */
export interface ItSupportAttachment {
  id: string;
  request_id: string;
  message_id: string | null;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  created_at: string;
}

/** 添付の保存先 Storage バケット（非公開）。 */
export const IT_ATTACHMENT_BUCKET = 'it-support-attachments';

/**
 * 保存パスから表示用の署名付きURLを発行する（1時間有効）。
 * バケットは非公開のため、公開URL（getPublicUrl）は使わない。
 * 権限がない・対象が見つからない場合は null を返す。
 */
export const createItSupportAttachmentSignedUrl = (
  storagePath: string,
  download = false,
): Promise<string | null> =>
  createStorageSignedUrl(IT_ATTACHMENT_BUCKET, storagePath, download);

/**
 * ITサポートの添付を削除する（Storage の実体 → DB行 の順）。
 * DB行を先に消すとストレージ側の DELETE ポリシー（行との突合）が通らなくなるため、この順序を守る。
 * アップロード者本人、または IT担当者のみ（最終判定は RLS）。
 */
export async function deleteItSupportAttachment(
  attachment: Pick<ItSupportAttachment, 'id' | 'storage_path'>,
): Promise<void> {
  try {
    const { error: storageError } = await supabase.storage
      .from(IT_ATTACHMENT_BUCKET)
      .remove([attachment.storage_path]);
    if (storageError) throw storageError;

    const { error } = await supabase
      .from('it_support_attachments')
      .delete()
      .eq('id', attachment.id);
    if (error) throw error;
  } finally {
    // 失敗してもキャッシュは捨てて、古いURLを残さない
    removeStorageSignedUrlCache(IT_ATTACHMENT_BUCKET, attachment.storage_path);
  }
}

/** 添付を付与できるメッセージ送信者種別（AI・システムは添付不可）。 */
export const IT_ATTACHABLE_SENDER_TYPES: ItSenderType[] = ['requester', 'it'];

/** ステータス変更（IT担当者向け）の選択肢。 */
export const IT_STATUS_OPTIONS: ItStatus[] = [
  'pending',
  'accepted',
  'in_progress',
  'waiting',
  'resolved',
  'closed',
];

/** スレッドで「システムメッセージ」として扱う sender_type。 */
export const isItSystemMessage = (senderType: string): boolean => senderType === 'system';

/**
 * 「よくある質問」の集計対象チャネル（§3.7）。
 * AIチャット（ai）とオンライン申請（online）の両方。不具合報告・要望は対象外。
 */
export const IT_FAQ_CHANNELS: ItChannel[] = ['online', 'ai'];

/** 「よくある質問」で集計する解決済み申請の取得上限件数。 */
export const IT_FAQ_MAX_RESOLVED = 500;

/** 意味ベースでまとめるときに AI へ一度に渡す代表質問の上限件数。 */
export const IT_FAQ_MAX_CLUSTER_ITEMS = 200;

/** 解決メモの最大文字数（解決登録モーダルの入力上限）。 */
export const IT_RESOLUTION_NOTE_MAX = 500;

/** 画面表示用の「よくある質問」1件。 */
export interface ItSupportFaqItem {
  key: string;
  question: string;
  count: number;
  answer: string;
  latestAt: string;
}

/**
 * 指定した申請IDについて、スレッド内の「回答」本文を返す。
 * IT担当者（it）・AI（ai）が送った最後のメッセージを回答として採用する。
 * 申請者自身の本文（＝質問文）やシステムメッセージは回答に含めない。
 *
 * resolution_note が空の申請で、申請本文（質問文）を回答として誤表示しないための補完に使う。
 * RLS により、申請者本人または IT担当者として閲覧できる申請のみが対象になる。
 */
export async function fetchLatestItAnswers(
  requestIds: string[],
): Promise<Record<string, string>> {
  const ids = Array.from(new Set(requestIds.filter((id): id is string => Boolean(id))));
  if (ids.length === 0) return {};
  try {
    const { data, error } = await supabase
      .from('it_support_messages')
      .select('request_id, sender_type, body, created_at')
      .in('request_id', ids)
      .in('sender_type', ['ai', 'it'])
      .order('created_at', { ascending: true });
    if (error) throw error;
    const map: Record<string, string> = {};
    for (const row of (data as { request_id: string; body: string | null }[]) || []) {
      const text = (row.body || '').trim();
      if (!text) continue;
      // created_at 昇順で取得しているため、最後に来たものが最新の回答になる
      map[row.request_id] = text;
    }
    return map;
  } catch (err) {
    console.warn('ITサポートの回答本文の取得に失敗:', err);
    return {};
  }
}

// ============================================================
// 対応ステータス（IT担当者の空き状況 / presence）
// ------------------------------------------------------------
// IT担当者ごとの空き状況を扱う（§3.8）。手動ステータスを最優先し、
// 未設定なら「公休」「休み」「対応時間外」「対応中（未完了申請の担当）」で自動判定する。
// ※ 自動判定専用（手動では選べない）: 公休 / 休み / 対応時間外
// ============================================================
export type ItPresenceStatus =
  | 'available'
  | 'busy'
  | 'off_hours'
  | 'public_holiday'
  | 'day_off'
  | 'away'
  | 'offline';

/**
 * 手動で選択・保存できるステータス（DBの許容値に一致）。
 * 公休 / 休み / 対応時間外 は自動判定専用のため含めない。
 */
export const IT_PRESENCE_OPTIONS: ItPresenceStatus[] = ['available', 'busy', 'away', 'offline'];

export const IT_PRESENCE_META: Record<
  ItPresenceStatus,
  { label: string; className: string; dot: string; icon: string }
> = {
  available: {
    label: '対応可能',
    className: 'bg-emerald-50 text-emerald-700',
    dot: 'bg-emerald-500',
    icon: 'ri-checkbox-circle-line',
  },
  busy: {
    label: '対応中',
    className: 'bg-[color:var(--theme-primary-light)] text-[color:var(--theme-primary)]',
    dot: 'bg-[color:var(--theme-primary)]',
    icon: 'ri-progress-4-line',
  },
  off_hours: {
    label: '対応時間外',
    className: 'bg-gray-100 text-gray-600',
    dot: 'bg-gray-400',
    icon: 'ri-moon-clear-line',
  },
  public_holiday: {
    label: '公休',
    className: 'bg-rose-50 text-rose-700',
    dot: 'bg-rose-400',
    icon: 'ri-calendar-event-line',
  },
  day_off: {
    label: '休み',
    className: 'bg-pink-50 text-pink-700',
    dot: 'bg-pink-400',
    icon: 'ri-hotel-bed-line',
  },
  away: {
    label: '離席・不在',
    className: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    icon: 'ri-walk-line',
  },
  offline: {
    label: 'オフライン',
    className: 'bg-gray-100 text-gray-500',
    dot: 'bg-gray-400',
    icon: 'ri-logout-circle-line',
  },
};

export const getItPresenceMeta = (status: string) =>
  IT_PRESENCE_META[status as ItPresenceStatus] ?? IT_PRESENCE_META.available;

/**
 * 経過時間を「〜前」で表す（1分未満は「たった今」）。
 * 空き待ちの待機開始からの経過時間や、空き状況の「最終更新」表示などに使う。
 * now を渡すと、定期的に再計算する表示（ライブ更新）でも同じ基準で計算できる。
 */
export const formatItElapsed = (iso?: string | null, now: number = Date.now()): string => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = now - then;
  if (diff < 60_000) return 'たった今';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}分前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour}時間前`;
  const day = Math.floor(hour / 24);
  return `${day}日前`;
};

/** it_support_presence の1行（手動ステータス）。 */
export interface ItSupportPresence {
  user_id: string;
  status: ItPresenceStatus;
  note: string | null;
  /**
   * 手動ステータスを設定した日（JST "YYYY-MM-DD"）。
   * 手動ステータスは「当日のみ有効」で、翌日になると自動判定へ戻る。
   */
  presence_date: string | null;
  updated_at: string;
}

// ============================================================
// 優先制御（対応順のソート・§3.8）
// ------------------------------------------------------------
// 既定は「早いもの優先」。例外で「緊急度が高い順」「ロール順（上位が先）」を先に見る。
// ソートキー: urgency_rank DESC → role_rank DESC → created_at ASC
// ============================================================
export const IT_URGENCY_RANK: Record<ItUrgency, number> = {
  low: 0,
  normal: 1,
  high: 2,
  urgent: 3,
};

/** ロール順（上位が先）。§10-3 の確定順に合わせる。 */
export const IT_ROLE_RANK: Record<string, number> = {
  general_admin: 5,
  welfare_supervisor: 4,
  welfare_admin: 3,
  welfare_staff: 2,
  general_user: 1,
  welfare_user: 0,
};

/** 申請者のロールから優先度（role_rank）を求める（旧ロール値は正規化してから引く）。 */
export const getItRoleRank = (role?: string | null): number =>
  IT_ROLE_RANK[normalizeAppRole(role ?? '') as string] ?? 0;

/** 申請の優先度（緊急度ランク・ロールランク）を返す。 */
export const getItRequestPriority = (request: ItSupportRequest) => ({
  urgency: IT_URGENCY_RANK[request.urgency] ?? IT_URGENCY_RANK.normal,
  role: getItRoleRank(request.requester?.role ?? null),
});

/**
 * 受付一覧の優先順比較（§3.8）。
 * 緊急度が高い順 → 申請者ロールが上位順 → 申請が早い順。
 */
export const compareItRequestsByPriority = (a: ItSupportRequest, b: ItSupportRequest): number => {
  const pa = getItRequestPriority(a);
  const pb = getItRequestPriority(b);
  if (pa.urgency !== pb.urgency) return pb.urgency - pa.urgency;
  if (pa.role !== pb.role) return pb.role - pa.role;
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
};

/** 受付一覧を優先順（§3.8）に並べ替えた新しい配列を返す（元配列は変更しない）。 */
export const sortItRequestsByPriority = (requests: ItSupportRequest[]): ItSupportRequest[] =>
  [...requests].sort(compareItRequestsByPriority);

// ============================================================
// 会議室（直接通話）バッジ / 空き待ち（B-4・B-5 / §3.6・§3.8）
// ============================================================

/**
 * 指定した会議室名のうち、現在アクティブ（会議中）なものを返す。
 * 会議室を持たない申請ばこびだ「空配列」を返す。RLS で見えない会議室は含まれない（その場合は「会議室あり」のみ表示）。
 */
export async function fetchActiveItMeetingRooms(roomNames: string[]): Promise<string[]> {
  const names = Array.from(new Set(roomNames.filter((n): n is string => Boolean(n))));
  if (names.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('meetings')
      .select('room_name')
      .in('room_name', names)
      .eq('status', 'active');
    if (error) throw error;
    return ((data as { room_name: string }[]) || []).map((r) => r.room_name);
  } catch (err) {
    console.warn('ITサポート会議室の取得に失敗:', err);
    return [];
  }
}

/** 空き待ち（it_support_waiting）のステータス。 */
export type ItWaitingStatus = 'waiting' | 'notified' | 'cancelled';

/** it_support_waiting の1行。 */
export interface ItSupportWaiting {
  id: string;
  request_id: string;
  requester_id: string;
  target_group: ItTargetGroup;
  status: ItWaitingStatus;
  created_at: string;
  updated_at: string;
  notified_at: string | null;
}